/**
 * UTM capture — snapshots the standard 5 Google/Meta/LinkedIn UTM params
 * from `window.location.search` on the very first page visit and keeps
 * them in sessionStorage for the duration of the visit. Every form
 * submission (Get Started, Site Audit) appends the captured UTMs so the
 * inbound source is preserved even after the visitor clicks through
 * several pages before submitting.
 *
 * Also captures `gclid` (Google Ads click id) — the single most valuable
 * attribution parameter, since it survives even when UTMs are stripped.
 */
const KEY = "redkey_utm_snapshot_v1";
const FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
];

/**
 * Read UTMs from the current URL and, if any are present, persist them
 * to sessionStorage. Non-destructive: first-touch wins so a mid-session
 * pageview without UTMs never overwrites an earlier attributed visit.
 */
export function captureUtmFromUrl() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    for (const f of FIELDS) {
      const v = params.get(f);
      if (v) found[f] = v.slice(0, 200);
    }
    if (Object.keys(found).length === 0) return;

    const existing = getUtmSnapshot();
    if (existing) return; // first-touch wins
    found.captured_at = new Date().toISOString();
    found.landing_path = window.location.pathname;
    window.sessionStorage.setItem(KEY, JSON.stringify(found));
  } catch (e) {
    // sessionStorage unavailable → nothing to persist, silently drop
    if (typeof console !== "undefined") {
      console.warn("[utm] capture skipped:", e);
    }
  }
}

export function getUtmSnapshot() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Returns a payload chunk to spread into any form POST body. Empty
 * object when we have nothing — safe to always include.
 */
export function utmPayload() {
  const snap = getUtmSnapshot() || {};
  const out = {};
  for (const f of FIELDS) {
    if (snap[f]) out[f] = snap[f];
  }
  return out;
}
